/**
 * usersApi — typed client for the real M02 IAM User Master backend
 * (NestJS `@Controller('v1/users')`, reached as `/api/users` via the Vite
 * `/api → /v1` proxy).
 *
 * Replaces the earlier mock `/admin/users` contract. Field names, HTTP verbs
 * (PATCH for update), optimistic-lock `version`, and the activate/deactivate/
 * unlock lifecycle endpoints all mirror the backend DTOs in
 * `backend/src/modules/m02-iam/dto/iam.dto.ts` and `user.controller.ts`.
 */
import { apiRequest } from './client.js';

/** §12 USER TYPE — matches backend USER_TYPES (iam.dto.ts). */
export const USER_TYPES = [
  'INTERNAL',
  'VENDOR',
  'PAINTER',
  'SUPPORT',
  'AUDITOR',
] as const;
export type UserType = (typeof USER_TYPES)[number];

/** §14 USER STATUS — matches backend UserStatus enum (iam.enums.ts). */
export const USER_STATUSES = [
  'ACTIVE',
  'INACTIVE',
  'LOCKED',
  'SUSPENDED',
  'ARCHIVED',
  'PASSWORD_EXPIRED',
  'PENDING_ACTIVATION',
] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** Human labels for the status codes (Title Case for display). */
export const USER_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  LOCKED: 'Locked',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
  PASSWORD_EXPIRED: 'Password Expired',
  PENDING_ACTIVATION: 'Pending Activation',
};

/** A user row/detail as returned by the backend (T014 user_master entity). */
export interface User {
  readonly userId: string;
  readonly tenantId?: string;
  readonly companyId: string | null;
  readonly vendorId: string | null;
  readonly employeeCode: string | null;
  readonly firstName: string;
  readonly middleName: string | null;
  readonly lastName: string;
  readonly displayName: string;
  readonly mobileNumber: string;
  readonly emailAddress: string;
  readonly userType: string;
  readonly department: string | null;
  readonly designation: string | null;
  readonly managerId: string | null;
  readonly defaultRoleId: string | null;
  readonly userStatus: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string | null;
  readonly version: number;
}

/** POST /v1/users body (CreateUserRequestDto). */
export interface CreateUserInput {
  readonly companyId?: string;
  readonly vendorId?: string;
  readonly employeeCode?: string;
  readonly firstName: string;
  readonly middleName?: string;
  readonly lastName: string;
  readonly displayName?: string;
  readonly mobileNumber: string;
  readonly emailAddress: string;
  readonly userType?: string;
  readonly department?: string;
  readonly designation?: string;
  readonly managerId?: string;
  readonly defaultRoleId?: string;
  readonly temporaryPassword: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate?: string;
}

/** PATCH /v1/users/:id body (UpdateUserRequestDto — includes lock version). */
export interface UpdateUserInput {
  readonly firstName?: string;
  readonly middleName?: string;
  readonly lastName?: string;
  readonly displayName?: string;
  readonly mobileNumber?: string;
  readonly emailAddress?: string;
  readonly department?: string;
  readonly designation?: string;
  readonly managerId?: string;
  readonly defaultRoleId?: string;
  readonly companyId?: string;
  readonly version: number;
}

export interface UserListParams {
  readonly search?: string;
  readonly userStatus?: string;
  readonly defaultRoleId?: string;
  readonly page?: number;
  readonly limit?: number;
}

export interface UserListResult {
  readonly data: readonly User[];
  readonly total: number;
}

type Tok = string | null | undefined;

export function listUsers(params: UserListParams = {}, token?: Tok): Promise<UserListResult> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.userStatus) qs.set('userStatus', params.userStatus);
  if (params.defaultRoleId) qs.set('defaultRoleId', params.defaultRoleId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<UserListResult>(`/users${suffix}`, { token });
}

export function getUser(userId: string, token?: Tok): Promise<User> {
  return apiRequest<User>(`/users/${userId}`, { token });
}

export function createUser(body: CreateUserInput, token?: Tok): Promise<User> {
  return apiRequest<User>('/users', { method: 'POST', token, body });
}

export function updateUser(userId: string, body: UpdateUserInput, token?: Tok): Promise<User> {
  return apiRequest<User>(`/users/${userId}`, { method: 'PATCH', token, body });
}

export function activateUser(userId: string, token?: Tok): Promise<User> {
  return apiRequest<User>(`/users/${userId}/activate`, { method: 'POST', token, body: {} });
}

export function deactivateUser(userId: string, token?: Tok): Promise<User> {
  return apiRequest<User>(`/users/${userId}/deactivate`, { method: 'POST', token, body: {} });
}

export function unlockUser(userId: string, token?: Tok): Promise<User> {
  return apiRequest<User>(`/users/${userId}/unlock`, { method: 'POST', token, body: {} });
}

/**
 * Issues a fresh temporary password and terminates the user's sessions. The
 * generated password is returned once and never stored in readable form, so the
 * caller must hand it to the user out of band.
 */
export function adminResetPassword(
  userId: string,
  token?: Tok,
): Promise<{ tempPassword: string }> {
  return apiRequest<{ tempPassword: string }>(
    `/users/${userId}/admin-reset-password`,
    { method: 'POST', token, body: {} },
  );
}

/**
 * Sets a password the administrator has chosen, so it can be agreed with the
 * holder rather than transcribed from screen. Server-side policy and reuse
 * history still apply; the password is never echoed back.
 */
export function adminSetPassword(
  userId: string,
  newPassword: string,
  token?: Tok,
): Promise<{ changed: true }> {
  return apiRequest<{ changed: true }>(
    `/users/${userId}/admin-set-password`,
    { method: 'POST', token, body: { newPassword } },
  );
}
